import { useState, useCallback, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Modal, Button, App } from 'antd'
import { ChevronRight, ChevronLeft, Download } from 'lucide-react'
import {
  // View Mode styles - Carousel single panel
  CarouselViewContainer,
  CarouselNavRow,
  CarouselArrowButton,
  CarouselPanel,
  CarouselImageArea,
  CarouselPanelImage,
  CarouselPanelPlaceholder,
  CarouselCaptionBox,
  CarouselPanelTitle,
  CarouselCaptionText,
  CarouselPanelNumber,
  CarouselDotsContainer,
  CarouselDot,
} from './styles/IdeaActionsStyles'

// Frame titles following JTBD narrative arc: Situation → Struggle → Solution → Outcome
const FRAME_TITLES = ['The Situation', 'The Struggle', 'The Solution', 'The Outcome']

const DEFAULT_CAPTIONS = [
  'It starts like any other day, until the moment arrives that changes everything.',
  "The old way isn't working anymore. Frustration builds as every attempt falls short.",
  'Then comes the breakthrough. A new possibility opens up.',
  'Success. What once felt impossible is now reality.',
]

interface StoryboardModalProps {
  isOpen: boolean
  onClose: () => void
  ideaLabel: string
  existingUrls?: string[]
  existingCaptions?: string[]
  existingTitle?: string
}

export function StoryboardModal({
  isOpen,
  onClose,
  ideaLabel,
  existingUrls,
  existingCaptions,
  existingTitle,
}: StoryboardModalProps) {
  const { notification: antdNotification } = App.useApp()

  // Captions shown read-only in carousel
  const [captions, setCaptions] = useState<string[]>(DEFAULT_CAPTIONS)

  // Current panel index for carousel navigation
  const [currentViewPanelIndex, setCurrentViewPanelIndex] = useState(0)

  // Export loading state
  const [isExporting, setIsExporting] = useState(false)

  // Track which carousel panels failed to load
  const [panelErrors, setPanelErrors] = useState([false, false, false, false])

  // Touch swipe support
  const [touchStartX, setTouchStartX] = useState<number | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX)
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX === null) return
      const diff = touchStartX - e.changedTouches[0].clientX
      if (Math.abs(diff) > 50) {
        if (diff > 0 && currentViewPanelIndex < 3) {
          setCurrentViewPanelIndex((prev) => prev + 1)
        } else if (diff < 0 && currentViewPanelIndex > 0) {
          setCurrentViewPanelIndex((prev) => prev - 1)
        }
      }
      setTouchStartX(null)
    },
    [touchStartX, currentViewPanelIndex]
  )

  // Arrow key navigation
  useEffect(() => {
    if (!isOpen) return undefined
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentViewPanelIndex((prev) => Math.max(0, prev - 1))
      } else if (e.key === 'ArrowRight') {
        setCurrentViewPanelIndex((prev) => Math.min(3, prev + 1))
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  // Sync captions and reset carousel when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setCaptions(
        existingCaptions && existingCaptions.length === 4 ? existingCaptions : DEFAULT_CAPTIONS
      )
      setCurrentViewPanelIndex(0)
      setPanelErrors([false, false, false, false])
    }
  }, [isOpen, existingCaptions])

  // Helper function to create a combined canvas from storyboard images with captions
  const createStoryboardCanvas = useCallback(async (): Promise<HTMLCanvasElement | null> => {
    if (!existingUrls || existingUrls.length !== 4) return null

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = url
      })
    }

    const calculateLinesNeeded = (
      ctx: CanvasRenderingContext2D,
      text: string,
      maxWidth: number
    ): number => {
      if (!text) return 1
      const words = text.split(' ')
      let line = ''
      let lineCount = 1

      for (const word of words) {
        const testLine = line + word + ' '
        const metrics = ctx.measureText(testLine)
        if (metrics.width > maxWidth && line !== '') {
          lineCount++
          line = word + ' '
        } else {
          line = testLine
        }
      }
      return lineCount
    }

    try {
      const images = await Promise.all(existingUrls.map(loadImage))

      const panelWidth = 720
      const panelHeight = 480
      const padding = 8
      const titleHeight = 32
      const lineHeight = 28
      const captionPadding = 16
      const maxCaptionWidth = panelWidth - 24

      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      if (!tempCtx) return null
      tempCtx.font = 'italic 24px Georgia, serif'

      const captionLinesPerPanel = captions.map((caption) =>
        calculateLinesNeeded(tempCtx, caption || '', maxCaptionWidth)
      )

      const row1MaxLines = Math.max(captionLinesPerPanel[0], captionLinesPerPanel[1], 2)
      const row2MaxLines = Math.max(captionLinesPerPanel[2], captionLinesPerPanel[3], 2)

      const row1CaptionHeight = titleHeight + row1MaxLines * lineHeight + captionPadding
      const row2CaptionHeight = titleHeight + row2MaxLines * lineHeight + captionPadding

      const row1TotalHeight = panelHeight + row1CaptionHeight
      const row2TotalHeight = panelHeight + row2CaptionHeight

      const canvasWidth = panelWidth * 2 + padding * 3
      const canvasHeight = row1TotalHeight + row2TotalHeight + padding * 3

      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)

      const positions = [
        { x: padding, y: padding, captionHeight: row1CaptionHeight },
        { x: padding * 2 + panelWidth, y: padding, captionHeight: row1CaptionHeight },
        { x: padding, y: padding * 2 + row1TotalHeight, captionHeight: row2CaptionHeight },
        {
          x: padding * 2 + panelWidth,
          y: padding * 2 + row1TotalHeight,
          captionHeight: row2CaptionHeight,
        },
      ]

      const frameTitles = ['The Situation', 'The Struggle', 'The Solution', 'The Outcome']

      images.forEach((img, index) => {
        const pos = positions[index]
        const captionHeight = pos.captionHeight

        ctx.drawImage(img, pos.x, pos.y, panelWidth, panelHeight)

        const badgeSize = 72
        const badgeX = pos.x + 16
        const badgeY = pos.y + 16
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
        ctx.beginPath()
        ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#FFFFFF'
        ctx.font = 'bold 40px Arial, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(index + 1), badgeX + badgeSize / 2, badgeY + badgeSize / 2)
        ctx.textAlign = 'left'
        ctx.textBaseline = 'alphabetic'

        const captionY = pos.y + panelHeight
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(pos.x, captionY, panelWidth, captionHeight)

        ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)'
        ctx.lineWidth = 1
        ctx.strokeRect(pos.x, captionY, panelWidth, captionHeight)

        ctx.fillStyle = '#888888'
        ctx.font = '600 20px system-ui, -apple-system, sans-serif'
        ctx.fillText(frameTitles[index], pos.x + 12, captionY + 24)

        const captionText = captions[index] || ''
        ctx.fillStyle = '#1a1a1a'
        ctx.font = '24px system-ui, -apple-system, sans-serif'

        const words = captionText.split(' ')
        let line = ''
        let lineY = captionY + titleHeight + lineHeight

        for (const word of words) {
          const testLine = line + word + ' '
          const metrics = ctx.measureText(testLine)
          if (metrics.width > maxCaptionWidth && line !== '') {
            ctx.fillText(line.trim(), pos.x + 12, lineY)
            line = word + ' '
            lineY += lineHeight
          } else {
            line = testLine
          }
        }
        if (line.trim()) {
          ctx.fillText(line.trim(), pos.x + 12, lineY)
        }
      })

      ctx.font = 'bold 28px Poppins, sans-serif'
      const logoText = 'bandos.ai'
      const logoMetrics = ctx.measureText(logoText)
      const logoPadding = 12
      const logoHeight = 36
      const logoX = canvasWidth - 20 - logoMetrics.width - logoPadding
      const logoY = 8

      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
      ctx.beginPath()
      const radius = 8
      ctx.roundRect(logoX, logoY, logoMetrics.width + logoPadding * 2, logoHeight, radius)
      ctx.fill()

      ctx.fillStyle = '#FFFFFF'
      ctx.textAlign = 'right'
      ctx.fillText(logoText, canvasWidth - 20, logoY + 26)

      return canvas
    } catch (error) {
      console.error('Failed to create storyboard canvas:', error)
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.ideaactions', action: 'create_storyboard_canvas' },
      })
      return null
    }
  }, [existingUrls, captions])

  const handleDownloadPNG = useCallback(async () => {
    setIsExporting(true)
    try {
      const canvas = await createStoryboardCanvas()
      if (!canvas) {
        antdNotification.error({
          message: 'Export failed',
          description: 'Could not create storyboard image. Please try again.',
          title: undefined,
        })
        return
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          antdNotification.error({
            message: 'Export failed',
            description: 'Could not generate image file.',
            title: undefined,
          })
          return
        }

        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const filename = (existingTitle ?? 'storyboard').replace(/[^a-z0-9]/gi, '_').toLowerCase()
        link.download = `${filename}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        antdNotification.success({
          message: 'Storyboard downloaded',
          description: 'Your storyboard has been saved as a PNG image.',
          title: undefined,
        })
      }, 'image/png')
    } catch (error) {
      console.error('Failed to download storyboard:', error)
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.ideaactions', action: 'download_storyboard' },
      })
      antdNotification.error({
        message: 'Export failed',
        description: 'An error occurred while exporting. Please try again.',
        title: undefined,
      })
    } finally {
      setIsExporting(false)
    }
  }, [createStoryboardCanvas, existingTitle, antdNotification])

  if (!existingUrls || existingUrls.length !== 4) return null

  const currentTitle = FRAME_TITLES[currentViewPanelIndex]

  return (
    <Modal
      title={
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: 24,
          }}
        >
          Storyboard: {ideaLabel}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      centered
      width="min(900px, calc(100vw - 32px))"
      styles={{
        body: {
          maxHeight: 'calc(100dvh - 180px)',
          overflowY: 'auto',
          padding: '0',
        },
        wrapper: {
          overflow: 'hidden',
        },
      }}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<Download size={16} />}
          onClick={handleDownloadPNG}
          loading={isExporting}
        >
          Download Storyboard
        </Button>,
      ]}
    >
      <CarouselViewContainer>
        <CarouselNavRow>
          <CarouselPanel onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <CarouselImageArea>
              <CarouselArrowButton
                $hidden={currentViewPanelIndex === 0}
                disabled={currentViewPanelIndex === 0}
                onClick={() => setCurrentViewPanelIndex((prev) => Math.max(0, prev - 1))}
                aria-label="Previous panel"
              >
                <ChevronLeft size={24} />
              </CarouselArrowButton>

              <CarouselPanelNumber>{currentViewPanelIndex + 1}</CarouselPanelNumber>
              {!panelErrors[currentViewPanelIndex] ? (
                <CarouselPanelImage
                  src={existingUrls[currentViewPanelIndex]}
                  alt={`Panel ${currentViewPanelIndex + 1}: ${currentTitle}`}
                  onError={() =>
                    setPanelErrors((prev) => {
                      const next = [...prev]
                      next[currentViewPanelIndex] = true
                      return next
                    })
                  }
                />
              ) : (
                <CarouselPanelPlaceholder>Image unavailable</CarouselPanelPlaceholder>
              )}

              <CarouselArrowButton
                $hidden={currentViewPanelIndex === 3}
                disabled={currentViewPanelIndex === 3}
                onClick={() => setCurrentViewPanelIndex((prev) => Math.min(3, prev + 1))}
                aria-label="Next panel"
              >
                <ChevronRight size={24} />
              </CarouselArrowButton>
            </CarouselImageArea>
            <CarouselCaptionBox>
              <CarouselPanelTitle>{currentTitle}</CarouselPanelTitle>
              <CarouselCaptionText>
                {captions[currentViewPanelIndex] || DEFAULT_CAPTIONS[currentViewPanelIndex]}
              </CarouselCaptionText>
            </CarouselCaptionBox>
          </CarouselPanel>
        </CarouselNavRow>

        <CarouselDotsContainer>
          {FRAME_TITLES.map((_, index) => (
            <CarouselDot
              key={index}
              $active={index === currentViewPanelIndex}
              onClick={() => setCurrentViewPanelIndex(index)}
              aria-label={`Go to panel ${index + 1}`}
            />
          ))}
        </CarouselDotsContainer>
      </CarouselViewContainer>
    </Modal>
  )
}
